<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseChart from '@/components/charts/BaseChart.vue';
import SectionTitle from '@/components/common/section-title.vue';
import { AnimatedContent } from '@/components/vuebits';

const { t } = useI18n();

/** 仅影响图表 option / 高度；布局用 CSS 断点 */
const isNarrow = ref(false);
let mq = null;

function syncNarrow(event) {
  isNarrow.value = event?.matches ?? mq?.matches ?? false;
}

onMounted(() => {
  mq = window.matchMedia('(max-width: 767px)');
  syncNarrow(mq);
  mq.addEventListener('change', syncNarrow);
});

onBeforeUnmount(() => {
  mq?.removeEventListener('change', syncNarrow);
  mq = null;
});

const pillars = computed(() => [
  { index: '01', title: t('flywheel.1Title'), desc: t('flywheel.1Desc') },
  { index: '02', title: t('flywheel.2Title'), desc: t('flywheel.2Desc') },
  { index: '03', title: t('flywheel.3Title'), desc: t('flywheel.3Desc') },
]);

const weekLabels = computed(() => [
  t('flywheel.chart.w1'),
  t('flywheel.chart.w2'),
  t('flywheel.chart.w3'),
  t('flywheel.chart.w4'),
  t('flywheel.chart.w5'),
  t('flywheel.chart.w6'),
  t('flywheel.chart.w7'),
  t('flywheel.chart.w8'),
]);

const chartHeight = computed(() => (isNarrow.value ? '200px' : '260px'));

/** 单折线：采纳率随使用周数抬升 */
const chartOption = computed(() => {
  const narrow = isNarrow.value;

  return {
    color: ['#155eef'],
    grid: {
      left: narrow ? 2 : 0,
      right: narrow ? 4 : 8,
      top: narrow ? 8 : 12,
      bottom: 0,
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255,255,255,0.96)',
      borderColor: '#e2e8f0',
      borderWidth: 1,
      textStyle: { color: '#0f172a', fontSize: 12 },
      valueFormatter: (v) => `${v}%`,
      confine: true,
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: weekLabels.value,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: '#94a3b8',
        fontSize: narrow ? 10 : 11,
        interval: narrow ? 1 : 0,
        hideOverlap: true,
      },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      splitNumber: narrow ? 2 : 4,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
      axisLabel: {
        color: '#94a3b8',
        fontSize: narrow ? 10 : 11,
        formatter: '{value}%',
      },
    },
    series: [
      {
        name: t('flywheel.chart.adoption'),
        type: 'line',
        smooth: 0.35,
        showSymbol: false,
        lineStyle: { width: 2.5 },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(21,94,239,0.18)' },
              { offset: 1, color: 'rgba(21,94,239,0)' },
            ],
          },
        },
        data: [5, 31, 35, 47, 53, 55, 62, 89],
      },
    ],
  };
});
</script>

<template>
  <section
    id="flywheel"
    class="relative overflow-hidden pb-[clamp(4rem,10vw,8rem)] pt-14 md:pt-20"
  >
    <div class="section-shell relative">
      <AnimatedContent :distance="24">
        <p class="eyebrow text-center">{{ t('flywheel.eyebrow') }}</p>
      </AnimatedContent>
      <SectionTitle :title="t('flywheel.title')" />
      <AnimatedContent :delay="0.05" :distance="20" class-name="-mt-7">
        <p class="mx-auto max-w-2xl text-center text-[15px] leading-7 text-muted-foreground sm:text-base md:text-lg">
          {{ t('flywheel.subtitle') }}
        </p>
      </AnimatedContent>

      <AnimatedContent :delay="0.1" :distance="28" class-name="mt-10 md:mt-14">
        <!-- 手机：图在上；md+ 保持原序（要点 → 图），lg 左右分栏 -->
        <div class="flex flex-col gap-8 md:grid md:gap-12 lg:grid-cols-12 lg:items-start lg:gap-16">
          <ul class="order-2 border-t border-slate-200/80 md:order-none lg:col-span-5">
            <li
              v-for="item in pillars"
              :key="item.index"
              class="border-b border-slate-200/80 py-4 md:py-5"
            >
              <div class="flex gap-3 md:items-baseline md:gap-4">
                <span class="mt-0.5 w-6 shrink-0 text-[12px] font-medium tabular-nums text-primary md:mt-0 md:w-7 md:text-[13px]">
                  {{ item.index }}
                </span>
                <div class="min-w-0 flex-1">
                  <h3 class="text-[15px] font-semibold tracking-tight text-foreground md:text-base">
                    {{ item.title }}
                  </h3>
                  <p class="mt-1 text-[13px] leading-6 text-muted-foreground md:mt-1.5 md:text-sm">
                    {{ item.desc }}
                  </p>
                </div>
              </div>
            </li>
          </ul>

          <div class="order-1 md:order-none lg:col-span-7">
            <p class="text-sm font-medium text-primary">{{ t('flywheel.chart.adoption') }}</p>
            <p class="mt-1 !text-[11px] leading-4 text-slate-400 md:text-xs opacity-30">
              {{ t('flywheel.chartNote') }}
            </p>
            <BaseChart class="mt-4 md:mt-5" :option="chartOption" :height="chartHeight" />
          </div>
        </div>
      </AnimatedContent>
    </div>
  </section>
</template>
